import type { Absence, CalendarSession, Planned } from "./api";

/**
 * Даты календаря: сетка месяца, подписи и разбор по дням.
 *
 * Всё считается в местном времени устройства и в формате `YYYY-MM-DD` — том
 * же, в котором план живёт на сервере. Через `Date` с часами эти даты водить
 * нельзя: в поясах восточнее Гринвича `toISOString()` отдаёт вчерашний день,
 * и план съезжает на сутки.
 */

export const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const MONTHS = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

const MONTHS_NOMINATIVE = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

/** `YYYY-MM-DD` из локальных полей даты, без ухода в UTC. */
export function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Дата из `YYYY-MM-DD` — полдень, чтобы переход на летнее время не сдвинул день. */
export function fromIso(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

/** Понедельник — 0, воскресенье — 6. У `Date.getDay()` неделя начинается с воскресенья. */
export const weekday = (date: Date): number => (date.getDay() + 6) % 7;

export function addDays(value: string, days: number): string {
  const date = fromIso(value);
  date.setDate(date.getDate() + days);
  return iso(date);
}

/** «28 сентября», «28 сентября 2027» — год только когда он не текущий. */
export function human(value: string, today?: string): string {
  const date = fromIso(value);
  const sameYear = !today || fromIso(today).getFullYear() === date.getFullYear();
  const tail = sameYear ? "" : ` ${date.getFullYear()}`;
  return `${date.getDate()} ${MONTHS[date.getMonth()]}${tail}`;
}

/** «Сентябрь 2026» — шапка сетки. */
export function monthTitle(value: string): string {
  const date = fromIso(value);
  return `${MONTHS_NOMINATIVE[date.getMonth()]} ${date.getFullYear()}`;
}

/** «Сегодня», «Завтра» или «в среду» — то, как человек говорит о ближайших днях. */
export function relative(value: string, today: string): string | null {
  if (value === today) return "Сегодня";
  if (value === addDays(today, 1)) return "Завтра";
  const days = Math.round((fromIso(value).getTime() - fromIso(today).getTime()) / 86400000);
  if (days > 1 && days < 7) {
    const names = ["в понедельник", "во вторник", "в среду", "в четверг",
                   "в пятницу", "в субботу", "в воскресенье"];
    return names[weekday(fromIso(value))];
  }
  return null;
}

/**
 * Сетка месяца: полные недели с понедельника по воскресенье.
 *
 * Дни соседних месяцев остаются в сетке, иначе строки разъезжаются и
 * календарь перестаёт читаться как календарь.
 */
export function monthGrid(anchor: string): string[] {
  const date = fromIso(anchor);
  const first = new Date(date.getFullYear(), date.getMonth(), 1, 12);
  const start = new Date(first);
  start.setDate(first.getDate() - weekday(first));

  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - weekday(last)));

  const days: string[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    days.push(iso(d));
  }
  return days;
}

export const sameMonth = (value: string, anchor: string): boolean =>
  fromIso(value).getMonth() === fromIso(anchor).getMonth();

/** Что происходит в конкретный день — всё, что нужно клетке сетки и списку. */
export interface DayFacts {
  planned: Planned[];
  sessions: CalendarSession[];
  away: boolean;
}

export function byDay(
  planned: Planned[],
  sessions: CalendarSession[],
  absences: Absence[],
): Map<string, DayFacts> {
  const map = new Map<string, DayFacts>();
  const get = (day: string): DayFacts => {
    let facts = map.get(day);
    if (!facts) {
      facts = { planned: [], sessions: [], away: covered(absences, day) };
      map.set(day, facts);
    }
    return facts;
  };

  for (const item of planned) get(item.date).planned.push(item);
  for (const item of sessions) get(item.date).sessions.push(item);
  return map;
}

export interface WeekCell {
  date: string;
  /** «Пн» — буквы недели той же азбукой, что в сетке календаря. */
  label: string;
  /** Число месяца. */
  day: number;
  /** Что с этим днём: сделано, по плану, не состоялась или ничего. */
  status: "done" | "planned" | "missed" | null;
  today: boolean;
  away: boolean;
}

/**
 * Семь дней от сегодня — полоса недели на главной.
 *
 * Полоса отвечает на вопрос, ради которого обычно и открывают календарь:
 * «что у меня на неделе». Она же — вход в него (19-home-and-bot-cards.md §2).
 *
 * Статус дня один, даже если тренировок в нём несколько: сделанное сильнее
 * запланированного, запланированное — сильнее пропущенного. Иначе в клетке
 * 6×6 пришлось бы рисовать три точки и объяснять их порядок.
 */
export function weekStrip(
  today: string,
  planned: Planned[],
  absences: Absence[] = [],
): WeekCell[] {
  const cells: WeekCell[] = [];
  for (let i = 0; i < 7; i += 1) {
    const date = addDays(today, i);
    const here = planned.filter((p) => p.date === date);
    const status = here.some((p) => p.status === "done")
      ? "done"
      : here.some((p) => p.status === "planned")
        ? "planned"
        : here.some((p) => p.status === "missed")
          ? "missed"
          : null;
    cells.push({
      date,
      label: WEEKDAY_LABELS[weekday(fromIso(date))],
      day: fromIso(date).getDate(),
      status,
      today: i === 0,
      away: covered(absences, date),
    });
  }
  return cells;
}

/** Ближайшая тренировка в полосе — строка «что дальше» под ней. */
export function nextInWeek(planned: Planned[], today: string): Planned | null {
  const ahead = planned
    .filter((p) => p.status === "planned" && p.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  return ahead[0] ?? null;
}

/** Накрыт ли день периодом «не тренируюсь». Открытый конец не кончается. */
export function covered(absences: Absence[], day: string): boolean {
  return absences.some((a) => day >= a.starts_on && (a.ends_on === null || day <= a.ends_on));
}

/** Период, накрывающий день, — чтобы показать «болеешь с такого-то». */
export function absenceAt(absences: Absence[], day: string): Absence | null {
  return absences.find((a) => day >= a.starts_on && (a.ends_on === null || day <= a.ends_on)) ?? null;
}

/**
 * Сколько плановых тренировок пропало за период болезни.
 *
 * Нужно ровно для одного вопроса — «пропущено 3 тренировки, сдвинуть план?»,
 * и потому считается по факту, а не по намерению: закрытые тренировки в счёт
 * не идут.
 */
export function missedDuring(planned: Planned[], absence: Absence, today: string): number {
  const end = absence.ends_on ?? today;
  return planned.filter(
    (p) => p.status !== "done" && p.date >= absence.starts_on && p.date <= end,
  ).length;
}
